import {
  getFormProps,
  getInputProps,
  getTextareaProps,
  useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node'
import {
  Form,
  json,
  useActionData,
  useLoaderData,
  type MetaFunction,
} from '@remix-run/react'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { GeneralErrorBoundary, NotFoundError } from '#app/components/errors.tsx'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { Layout } from '#app/components/layout.tsx'
import { Button } from '#app/components/ui/button.tsx'
import type { RootLoaderType } from '#app/root'
import type { Handle } from '#app/types/index.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { defaultLocale, getLocaleFromRequest } from '#app/utils/i18n.ts'
import { i18next } from '#app/utils/i18next.server.ts'
import { mapImage } from '#app/utils/mappers.ts'
import { getUrl, useIsPending } from '#app/utils/misc.tsx'
import { getSocialMetas } from '#app/utils/seo.ts'
import { getAllVacancies, getVacancyBySlug } from '#app/utils/strapi.ts'
import {
  getEmailSchema,
  getMessageSchema,
  getNameSchema,
  getPhoneSchema,
} from '#app/utils/validation.ts'

import { routes } from './vacatures.$slug._index.tsx'

export const handle: Handle = {
  getSitemapEntries: async request => {
    const locale = getLocaleFromRequest(request)
    const pages = await getAllVacancies(locale)
    return (pages || []).map(page => ({
      route: `${locale === defaultLocale ? '' : `/${locale}`}/${routes[locale]}/${page.attributes.slug}/${routes[locale]}`,
      priority: 0.6,
    }))
  },
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  if (!params.slug) {
    throw new Error('No slug provided to fetch vacancy with.')
  }

  const locale = getLocaleFromRequest(request)
  const vacancy = await getVacancyBySlug(locale, params.slug)

  if (!vacancy) {
    throw new Response('Not found', { status: 404 })
  }

  return json({
    vacancy,
  })
}

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  data,
  matches,
}) => {
  const rootData = matches.find(m => m.id === 'root')?.data

  if (data.vacancy.SEO) {
    const meta = data.vacancy.SEO
    return getSocialMetas({
      title: `${rootData.locale === defaultLocale ? 'Soliciteer voor ' : 'Apply for '}${meta?.title}`,
      description: meta?.description,
      image: meta?.image.data?.attributes.url,
      url: getUrl(rootData.requestInfo),
    })
  } else {
    return [
      { title: 'Not Found' },
      { name: 'description', content: 'Not found' },
    ]
  }
}

function getApplyFormSchema(t: TFunction, locale: string) {
  return z.object({
    name: getNameSchema(t, locale),
    email: getEmailSchema(t, locale),
    phone: getPhoneSchema(t, locale),
  })
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const locale = getLocaleFromRequest(request)
  const t = await i18next.getFixedT(request)

  const submission = await parseWithZod(formData, {
    schema: getApplyFormSchema(t, locale),
    async: true,
  })
  if (submission.status !== 'success' || !submission.value) {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const role = formData.get('role').toString()
  const { email, name, phone } = submission.value

  await sendEmail({
    to: 'meike@restaurantv12.nl',
    subject: `${name} heeft gesolliciteerd voor ${role}`,
    react: <ApplyEmail name={name} email={email} phone={phone} role={role} />,
  })

  return json({ result: submission.reply() })
}

export function ApplyEmail({
  name,
  email,
  phone,
  role,
}: {
  name: string
  email: string
  phone: string
  role: string
}) {
  return (
    <E.Html lang="nl" dir="ltr">
      <E.Container>
        <E.Heading as="h1">
          {name} heeft gesollicteerd voor {role}
        </E.Heading>
        <br />
        <p>
          <E.Text>{email}</E.Text>
          <E.Text>{phone}</E.Text>
        </p>
      </E.Container>
    </E.Html>
  )
}

export default function VacancyApplyRoute() {
  const data = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const { t, i18n } = useTranslation()
  const isPending = useIsPending()

  const image = mapImage(data.vacancy.image.data?.attributes)

  const [form, fields] = useForm({
    id: 'contact-form',
    constraint: getZodConstraint(getApplyFormSchema(t, i18n.language)),
    // @ts-expect-error - not sure why this is not working anymore
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, {
        schema: getApplyFormSchema(t, i18n.language),
      })
    },
    shouldRevalidate: 'onBlur',
  })

  if (actionData?.result.status === 'success') {
    return (
      <Layout imageSrc={image.url} imageAlt={image.alt}>
        <div className="text-center">
          <span className="text-h2">🎉</span>
          <p className="block text-center text-h2 font-bold text-primary">
            {t('form.apply.success')}
          </p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout imageSrc={image.url} imageAlt={image.alt}>
      <Form method="POST" className="max-w-[575px]" {...getFormProps(form)}>
        <h1 className="mb-12 text-h3 font-bold text-primary lg:text-h2">
          {t('vacancy.apply')} {data.vacancy.title}
        </h1>
        <input type="hidden" name="role" value={data.vacancy.title} />
        <Field
          labelProps={{ children: t('form.name.label') }}
          inputProps={{
            ...getInputProps(fields.name, { type: 'text' }),
            autoComplete: 'name',
          }}
          errors={fields.name.errors}
        />
        <Field
          labelProps={{ children: t('form.email.label') }}
          inputProps={{
            ...getInputProps(fields.email, { type: 'email' }),
            autoComplete: 'email',
          }}
          errors={fields.email.errors}
        />
        <Field
          labelProps={{ children: t('form.phone.label') }}
          inputProps={{
            ...getInputProps(fields.phone, { type: 'tel' }),
            autoComplete: 'tel',
          }}
          errors={fields.phone.errors}
        />
        <ErrorList errors={form.errors} id={form.errorId} />
        <Button
          type="submit"
          className="mt-6 w-full"
          size="lg"
          disabled={isPending}
        >
          {isPending ? t('cta.sending') : t('cta.send')}
        </Button>
      </Form>
    </Layout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary statusHandlers={{ 404: NotFoundError }} />
}
